// R9 Course brief: what the user reads while scene 1 builds — the recipe, objectives, themes, each scene's narration.
// Data = the reasoning-agent /state the build chat already loads (an older mw sends none: nothing renders).
import React from "react";
import i18n from "../../../locales/i18n";

const strings = (x) => (Array.isArray(x) ? x.filter((o) => typeof o === "string") : []);
const label = (x) => (typeof x === "string" && x.trim() ? x.trim() : null);
const count = (x) => (typeof x === "number" && x > 0 ? x : null);

export function briefFromState(data) {
  const scenes = Array.isArray(data && data.scenePlan && data.scenePlan.scenes)
    ? data.scenePlan.scenes
    : [];
  return {
    objectives: strings(data && data.objectives),
    keyPoints: strings(data && data.keyPoints),
    scenes: scenes.filter(
      (s) => s && typeof s.title === "string" && typeof s.narration === "string"
    ),
  };
}

// The recipe the user approved (/state `decisions`): only the display labels mw captured at pick time, never raw ids.
export function recipeFromState(data) {
  const d = (data && data.decisions) || {};
  const brand = d.brandColors && d.brandColors.apply ? label(d.brandColors.brandName) : null;
  return {
    flow: d.agentFlow ? label(d.agentFlow.label) : null,
    format: d.visualFormat ? label(d.visualFormat.label) : null,
    style: label(d.imageStyleLabel),
    language: label(d.language),
    durationMin: count(d.durationMin),
    sceneCount: count(d.sceneCount),
    voice: label(d.voiceLabel),
    brand,
  };
}

// /newchat model gear (decisions.modelPrefs, mw normalizeModelPrefs): only roles the user re-modelled; absent = mw default (unknown here, not shown).
export const MODEL_ROLES = ["drafter", "narrator", "planner", "sceneBuilder", "motion"];
// ponytail: the names the console model gear shows (aidea.console app/lib/model-prefs.ts MODELS); an id added later shows raw.
const MODEL_LABELS = {
  "claude-sonnet-5.5": "Sonnet 5.5",
  "claude-opus-5.5": "Opus 5.5",
  "gpt-6": "Astra",
  "gpt-6.1": "GPT Sol",
};
export function modelsFromState(data) {
  const prefs = data && data.decisions && data.decisions.modelPrefs;
  if (!prefs || typeof prefs !== "object") return [];
  return MODEL_ROLES.filter((r) => prefs[r] && label(prefs[r].model)).map((role) => ({
    role,
    model: MODEL_LABELS[prefs[role].model] || prefs[role].model,
    effort: label(prefs[role].effort),
  }));
}

const languageName = (code) => {
  try {
    return new Intl.DisplayNames([i18n.language || "en"], { type: "language" }).of(code) || code;
  } catch (e) {
    return code;
  }
};

function RecipeRows({ recipe, scenes }) {
  const t = (k, o) => i18n.t(`aideaChat.buildBrief.${k}`, o);
  const n = recipe.sceneCount || scenes;
  const rows = [
    ["flow", recipe.flow],
    ["format", recipe.format],
    ["style", recipe.style],
    ["language", recipe.language && languageName(recipe.language)],
    ["length", recipe.durationMin && t("minutes", { count: recipe.durationMin })],
    ["sceneCount", n && t("sceneCount", { count: n })],
    ["voice", recipe.voice],
    ["brand", recipe.brand],
  ].filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <>
      <h4>{t("recipe")}</h4>
      <dl className="aidea-build-brief-recipe">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{t(`recipeKeys.${k}`)}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}

// Consecutive scenes sharing a title (a section's scenes) fold under one heading; n = 1-based scene number.
function sceneGroups(scenes) {
  const groups = [];
  scenes.forEach((s, i) => {
    const last = groups[groups.length - 1];
    const item = { n: i + 1, narration: s.narration };
    if (last && last.title === s.title) last.items.push(item);
    else groups.push({ title: s.title, items: [item] });
  });
  return groups;
}

export function CourseBriefPanel({ brief }) {
  const t = (k, o) => i18n.t(`aideaChat.buildBrief.${k}`, o);
  const recipe = (brief && brief.recipe) || {};
  const hasRecipe = Object.values(recipe).some(Boolean);
  if (
    !brief ||
    (!hasRecipe && !brief.objectives.length && !brief.keyPoints.length && !brief.scenes.length)
  )
    return null;
  return (
    <section className="aidea-build-brief" aria-label={t("title")}>
      <h3>{t("title")}</h3>
      {hasRecipe && <RecipeRows recipe={recipe} scenes={brief.scenes.length} />}
      {brief.objectives.length > 0 && (
        <>
          <h4>{t("objectives")}</h4>
          <ol>
            {brief.objectives.map((o, i) => (
              <li key={i}>{o}</li>
            ))}
          </ol>
        </>
      )}
      {brief.keyPoints.length > 0 && (
        <>
          <h4>{t("themes")}</h4>
          <ul className="aidea-build-brief-themes">
            {brief.keyPoints.map((k, i) => (
              <li key={i}>{k}</li>
            ))}
          </ul>
        </>
      )}
      {brief.scenes.length > 0 && (
        <>
          <h4>{t("scenes")}</h4>
          <ol>
            {sceneGroups(brief.scenes).map((g) =>
              g.items.length === 1 ? (
                <li key={g.items[0].n}>
                  <details>
                    <summary>{g.title}</summary>
                    <p>{g.items[0].narration}</p>
                  </details>
                </li>
              ) : (
                <li key={g.items[0].n} className="aidea-build-brief-group">
                  <strong>{g.title}</strong>
                  {g.items.map((s) => (
                    <details key={s.n}>
                      <summary>{t("sceneN", { n: s.n })}</summary>
                      <p>{s.narration}</p>
                    </details>
                  ))}
                </li>
              )
            )}
          </ol>
        </>
      )}
    </section>
  );
}
